import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Flex, Typography, Segmented } from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  HolderOutlined,
  CloseOutlined,
} from "@ant-design/icons";

import SbButton from "uiCore/SbButton/SbButton";
import SbSpin from "uiCore/SbSpin/SbSpin";
import SbCard from "uiCore/SbCard/SbCard";
import SbDivider from "uiCore/SbDivider/SbDivider";
import { VIEWMODE } from "constants/markdown.constants";
import CoreMarkdownEditor from "Components/SbMarkdownEditor/CoreMarkdownEditor";
import { getJdTemplate, updateJdTemplate, getJdMergeFields } from "Api/apiService";
import EditTemplateMetaModal from "./EditTemplateMetaModal";
import {
  PageWrapperStyled,
  DragIconStyled,
  SectionTagStyled,
  EditorGridStyled,
  EditorPanelStyled,
  EditorPanelHeaderStyled,
  FooterRowStyled,
} from "./JdTemplateDetail.styles";

const { Title, Text } = Typography;

const JdTemplateDetail = () => {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const { state } = useLocation();
  const isNew = id === "new";

  const [templateName, setTemplateName] = useState(isNew ? (state?.templateName ?? "") : "");
  const [sections, setSections]         = useState([]);
  const [activeTab, setActiveTab]       = useState("MDX Editor");
  const [liveMarkdown, setLiveMarkdown] = useState(isNew ? (state?.markdown ?? "") : "");
  const [initialMd, setInitialMd]       = useState(isNew ? (state?.markdown ?? "") : "");
  const [templateMeta, setTemplateMeta] = useState({
    legalEntity: isNew ? (state?.legalEntity ?? "") : "",
    country:     isNew ? (state?.country     ?? "") : "",
  });
  const [loading, setLoading]           = useState(!isNew);
  const [saving, setSaving]                     = useState(false);
  const [metaModalOpen, setMetaModalOpen] = useState(false);

  // Always stored as grouped: Array<{ group: string|null, fields: Array<{ label, value }> }>
  const [mergeFields, setMergeFields] = useState([]);

  const editorPanelRef  = useRef(null);
  const previewPanelRef = useRef(null);
  const syncTimerRef    = useRef(null);

  // ── Fetch merge fields ───────────────────────────────────────────────────────
  useEffect(() => {
    getJdMergeFields()
      .then((data) => {
        // data may be the raw array directly OR wrapped in { merge_fields: [...] }
        const raw = Array.isArray(data) ? data : (data?.merge_fields ?? []);
        setMergeFields(raw);
      })
      .catch((err) => console.error("Failed to fetch merge fields:", err));
  }, []);

  // ── Fetch template (edit mode) ───────────────────────────────────────────────
  useEffect(() => {
    if (id === "new") return;
    setLoading(true);
    getJdTemplate(id)
      .then((data) => {
        const serverMd = data.org_jd_template_md ?? "";
        const md = (!serverMd && state?.markdown) ? state.markdown : serverMd;
        setTemplateName(data.org_jd_template_name ?? "");
        setLiveMarkdown(md);
        setInitialMd(md);
        setTemplateMeta({
          legalEntity: data.legalEntity           ?? "",
          country:     data.sb_org_template_value ?? "",
        });
        setSections(data.sections ?? []);
      })
      .catch((err) => console.error("Failed to fetch template:", err))
      .finally(() => setLoading(false));
  }, [id]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleMetaSave = (values) => {
    setTemplateName(values.templateName);
    setTemplateMeta({ legalEntity: values.legalEntity, country: values.country });
    setMetaModalOpen(false);
    updateJdTemplate(id, {
      templateName: values.templateName,
      country:      values.country,
      markdown:     liveMarkdown,
    }).catch((err) => console.error("Failed to save template meta:", err));
  };

  const handleEditorChange = useCallback((md) => {
    setLiveMarkdown(md);
    clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      previewPanelRef.current?.setMarkdown(md);
    }, 300);
  }, []);

  const handlePreviewChange = useCallback((md) => {
    setLiveMarkdown(md);
    clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      editorPanelRef.current?.setMarkdown(md);
    }, 300);
  }, []);

  const handleDeleteSection = (sectionId) => {
    setSections((prev) => prev.filter((s) => s.id !== sectionId));
  };

  const handleSave = () => {
    setSaving(true);
    updateJdTemplate(id, {
      templateName,
      country:  templateMeta.country,
      markdown: liveMarkdown,
    })
      .then(() => navigate(-1))
      .catch((err) => console.error("Failed to save template:", err))
      .finally(() => setSaving(false));
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <PageWrapperStyled>
      <SbSpin spinning={loading}>

        <Flex justify="space-between" align="center" style={{ marginBottom: 10 }}>
          <Flex align="center" gap={16}>
            <SbButton
              buttonType="action"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
            />

            <Flex vertical>
              <Title level={4} style={{ margin: 0 }}>JD Template — {templateName}</Title>
              <Text type="secondary">
                {templateMeta.country} · {sections.length} sections
              </Text>
            </Flex>

            {!isNew && (
              <SbButton
                buttonType="action"
                icon={<EditOutlined />}
                title="Edit details"
                onClick={() => setMetaModalOpen(true)}
              />
            )}
          </Flex>

          <Flex align="center" gap={12}>
            <Segmented
              options={["Sections", "MDX Editor"]}
              value={activeTab}
              onChange={setActiveTab}
            />
            <SbButton
              buttonType="action"
              icon={<CloseOutlined />}
              onClick={() => navigate(-1)}
            />
          </Flex>
        </Flex>

        <EditTemplateMetaModal
          open={metaModalOpen}
          onClose={() => setMetaModalOpen(false)}
          onSave={handleMetaSave}
          initialValues={{ templateName, country: templateMeta.country }}
        />

        {activeTab === "Sections" && !loading && (
          <SbCard>
            <Flex justify="space-between" align="center" style={{ marginBottom: 20 }}>
              <Text strong>{templateName}</Text>
              <Flex gap={10}>
                <SbButton buttonType="secondary" icon={<EyeOutlined />} onClick={() => setActiveTab("MDX Editor")}>
                  Preview
                </SbButton>
                <SbButton buttonType="primary" icon={<PlusOutlined />}>
                  Add Section
                </SbButton>
              </Flex>
            </Flex>

            <SbDivider style={{ margin: "0 0 8px 0" }} />

            {sections.map((section, index) => (
              <div key={section.id}>
                <Flex justify="space-between" align="center" style={{ padding: "14px 4px" }}>
                  <Flex align="center" gap={12}>
                    <DragIconStyled><HolderOutlined /></DragIconStyled>
                    <Text type="secondary">{index + 1}</Text>
                    <Text strong>{section.name}</Text>
                  </Flex>
                  <Flex align="center" gap={12}>
                    <SectionTagStyled $required={section.required}>
                      {section.required ? "Required" : "Optional"}
                    </SectionTagStyled>
                    <SbButton buttonType="action" icon={<EditOutlined />} />
                    <SbButton
                      buttonType="action"
                      icon={<DeleteOutlined />}
                      danger
                      onClick={() => handleDeleteSection(section.id)}
                    />
                  </Flex>
                </Flex>
                {index < sections.length - 1 && <SbDivider style={{ margin: 0 }} />}
              </div>
            ))}
          </SbCard>
        )}

        {activeTab === "MDX Editor" && !loading && (
          <>
            <FooterRowStyled>
              <SbButton buttonType="secondary" onClick={() => navigate(-1)}>
                Cancel
              </SbButton>
              <SbButton buttonType="primary" loading={saving} onClick={handleSave}>
                Save
              </SbButton>
            </FooterRowStyled>

            <EditorGridStyled>
              <EditorPanelStyled>
                <EditorPanelHeaderStyled>
                  <EditOutlined /> MDX EDITOR
                </EditorPanelHeaderStyled>
                <CoreMarkdownEditor
                  ref={editorPanelRef}
                  initialMarkdown={initialMd}
                  viewMode={VIEWMODE.EDIT}
                  initialViewType="source"
                  onChange={handleEditorChange}
                  mergeFields={mergeFields}
                  showInsertImage={false}
                  lockImageUrls={true}
                />
              </EditorPanelStyled>

              <EditorPanelStyled>
                <EditorPanelHeaderStyled>
                  <EyeOutlined /> PREVIEW
                </EditorPanelHeaderStyled>
                <CoreMarkdownEditor
                  ref={previewPanelRef}
                  initialMarkdown={initialMd}
                  viewMode={VIEWMODE.EDIT}
                  initialViewType="rich-text"
                  onChange={handlePreviewChange}
                  mergeFields={mergeFields}
                  showInsertImage={false}
                  lockImageUrls={true}
                />
              </EditorPanelStyled>
            </EditorGridStyled>
          </>
        )}

      </SbSpin>
    </PageWrapperStyled>
  );
};

export default JdTemplateDetail;